import { Switch } from '@/components/atom/Switch';
import { TextDetailRow } from '@/components/atom/TextDetailRow';
import { TokenUnit } from '@/components/atom/TokenUnit';
import { FulfillPath } from '@/components/molecule/FulfillPath';
import { Modal, ModalProps } from '@/components/molecule/Modal';
import { SelectDropdown } from '@/components/molecule/SelectDropdown';
import { UnitInput } from '@/components/molecule/UnitInput';
import { Events, exchangeDataAtom, marketOrdersAtom } from '@/state/app';
import { BigNumber, ethers } from 'ethers';
import { InfoEmpty } from 'iconoir-react';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { Image } from '../../atom/Image';
import { ExchangeOrder, OrderData } from '@/constants/types';
import { getOrderInfo } from '@/components/exchange/orders/Order';
import { ExchangeAction } from '@/components/exchange/ExchangeAction';
import { useExchangeContracts } from '@/hooks/exchange/contracts';
import { formatContractOrders } from '@/hooks/exchange/orders';
import { format } from 'date-fns';

type FulfillOrderProps = Pick<ModalProps, 'isVisible' | 'onClose'> & {
    orderId: number | null;
};

const defaultOrderData = (order: ExchangeOrder | null, info: ReturnType<typeof getOrderInfo>): OrderData => ({
    _id: order?._id,
    amount: order?.allowPartial ? BigNumber.from(0) : order?.amountToFulfill,
    amountLiquidToken: order?.allowPartial ? BigNumber.from(0) : order?.amount,
    amountTokenReceived: order?.allowPartial
        ? BigNumber.from(0)
        : order?.amount.mul(info!.exchangeRate!).div(ethers.utils.parseEther('1')),
    startUnstaking: !!order && order.startTime > 0,
    maxValue: order?.amountToFulfill,
    minValue: order?.allowPartial ? BigNumber.from(0) : order?.amountToFulfill,
});

// TODO: adjust design when order claimed, expired or cancelled after loading
const FulfillOrderModal = ({ orderId, isVisible, onClose }: FulfillOrderProps) => {
    const { getOrder } = useExchangeContracts();
    const exchangeData = useRecoilValue(exchangeDataAtom);
    const [marketOrders, setMarketOrders] = useRecoilState(marketOrdersAtom);
    const { orders } = marketOrders;

    const order = orderId ? orders[orderId] : null;
    const info = useMemo(() => getOrderInfo({ order: order, exchangeData }), [exchangeData.update, order]);

    const [orderData, setOrderData] = useState<OrderData>(defaultOrderData(order, info));
    const [state, setState] = useState({ update: 0 });

    const primaryInfo = info ? exchangeData.tokens[info.primary!._id] : null;

    // Updates info from contract, form will be disabled until fresh data is received
    useEffect(() => {
        updateOrder().then();
    }, []);

    const updateOrder = async () => {
        let _order = await getOrder(order!._id);
        _order = formatContractOrders([_order], undefined, true)[0];
        setMarketOrders((prev) => ({
            orders: { ...prev.orders, [_order._id]: _order },
            updateAPI: prev.updateAPI,
            update: new Date().getTime(),
        }));
        setState({ update: new Date().getTime() });
        return _order.canFulfill;
    };

    const handleInputUpdate = (amount: BigNumber) => {
        const amountLiquidToken = amount.eq(order!.amountToFulfill)
            ? order!.amount
            : amount.mul(ethers.utils.parseEther('1')).div(info!.discountedRate!);
        const amountTokenReceived = amountLiquidToken.mul(info!.exchangeRate!).div(ethers.utils.parseEther('1'));
        setOrderData((prev) => ({ ...prev, amount, amountLiquidToken, amountTokenReceived }));
    };

    const FulfillOrderBody = () => {
        return !order || !info ? null : (
            <div className="flex flex-col space-y-4">
                <UnitInput
                    symbol={info.primary!.tokenName}
                    balance={primaryInfo?.balance}
                    maxValue={
                        primaryInfo?.balance?.gt(order.amountToFulfill) ? order.amountToFulfill : primaryInfo?.balance
                    }
                    showEmptyBalance={true}
                    onUpdate={(amount) => handleInputUpdate(amount)}
                    label={
                        // TODO remove title do EXPIRED design
                        <span className=" t__subtitle--s2">{order.canFulfill ? '' : 'EXPIRED'} Amount to Fulfill</span>
                    }
                    secondaryLabel={
                        <SelectDropdown
                            variant="pill"
                            className="p-1"
                            title={info.primary!.tokenName}
                            isReadonly
                            prefix={<Image isIcon asset={info.primary!.tokenAsset} height={24} width={24} />}
                        />
                    }
                    labelAction={
                        <div className="flex space-x-1 t__body--b3 text-grey-30">
                            <span>Max Amount :</span>
                            <TokenUnit value={order.amountToFulfill} symbol={info.primary!.tokenName} />
                        </div>
                    }
                    isReadonly={!order.allowPartial || state.update == 0}
                    value={order.allowPartial ? undefined : order.amountToFulfill}
                />
                <div className="flex flex-col px-4 py-4 space-y-2 rounded-lg bg-black/20">
                    {order.startTime > 0 && (
                        // <TextDetailRow
                        //     caption="Unbonding Time Left"
                        //     value={
                        //         // TODO UNBONDING add logic
                        //         <span>3 - 5 days</span>
                        //     }
                        // />
                        <TextDetailRow
                            caption="Unbonding Start Time"
                            value={<span>{format(order.startTime * 1000, 'yyyy/MM/dd HH:mm')}</span>}
                        />
                    )}
                    {/*<TextDetailRow*/}
                    {/*    caption="APY"*/}
                    {/*    // TODO APY add logic*/}
                    {/*    value={<span>10%</span>}*/}
                    {/*/>*/}
                    <TextDetailRow
                        caption="Discount"
                        value={
                            <div className="flex space-x-1 text-teal-50">
                                <span>{Math.round(order.fee * 100 * 100) / 100}%</span>
                            </div>
                        }
                    />
                    <TextDetailRow
                        className="text-grey-20"
                        caption="Discounted Rate"
                        value={
                            <div className="flex justify-center space-x-2">
                                <div className="flex justify-center space-x-1">
                                    <Image isIcon asset={info.token!.tokenAsset!} height={20} width={20} />
                                    <TokenUnit value={ethers.constants.WeiPerEther} />
                                </div>
                                <span>=</span>
                                <div className="flex justify-center space-x-1">
                                    <Image isIcon asset={info.primary!.tokenAsset!} height={20} width={20} />
                                    {/*TODO precision only displaying 3*/}
                                    <TokenUnit value={info.discountedRate} precision={6} />
                                </div>
                            </div>
                        }
                    />
                    <TextDetailRow
                        className="text-grey-20"
                        caption="Current Rate"
                        value={
                            <div className="flex justify-center space-x-2">
                                <div className="flex justify-center space-x-1">
                                    <Image isIcon asset={info.token!.tokenAsset!} height={20} width={20} />
                                    <TokenUnit value={ethers.constants.WeiPerEther} />
                                </div>
                                <span>=</span>
                                <div className="flex justify-center space-x-1">
                                    <Image isIcon asset={info.primary!.tokenAsset!} height={20} width={20} />
                                    <TokenUnit value={info.exchangeRate} precision={6} />
                                </div>
                            </div>
                        }
                    />
                    {order.startTime == 0 && (
                        <TextDetailRow
                            caption="Start Unstaking"
                            value={
                                <Switch
                                    value={orderData?.startUnstaking ?? false}
                                    onSwitch={(val) => {
                                        setOrderData((prev) => ({
                                            ...prev,
                                            startUnstaking: val,
                                        }));
                                    }}
                                    variant="teal"
                                />
                            }
                        />
                    )}
                </div>
                <FulfillPath
                    order={order}
                    orderData={orderData}
                    info={info}
                    // TODO calculation of time remaining with unbodn estimation
                    unstakingTime={3600 * 48}
                />
                <div className="flex items-center justify-start px-4 space-x-2 text-grey-20 t__caption--c1">
                    <InfoEmpty className="inline-block -mt-1" width={16} height={16} strokeWidth={1.75} fr="true" />
                    <span>
                        {orderData?.startUnstaking
                            ? 'Upon the fulfilment you will find a claim order in Claims Tab.'
                            : 'The tokens will be immediately transferred to your wallet upon fulfilment of the order'}
                    </span>
                </div>
                <ExchangeAction
                    event={Events.orderFulfillEvent}
                    token={info.primary!._id}
                    uniqueId={info.uniqueId}
                    orderData={orderData}
                    onSuccess={onClose}
                    isDisabled={state.update == 0 || !order.canFulfill}
                    validate={updateOrder}
                />
            </div>
        );
    };

    return (
        <Modal
            isVisible={isVisible}
            isClosable={true}
            isArrowOutside={false}
            onClose={onClose}
            title="Fulfill Order"
            titleAlign="left"
            bodyPadding="p-4 md:p-6"
            titlePadding="p-4 md:p-6"
            className=" bg-gradient-to-b from-teal-60/20 via-grey-60 to-grey-60"
            body={<div>{FulfillOrderBody()}</div>}
        />
    );
};

export default FulfillOrderModal;
